import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { availabilityOverviewQuery, formatPercent } from '../../api/appAvailability'
import { moduleOf, shellQuery } from '../../api/shell'
import { BoardCard, BoardGrid, BoardList, BoardLook, BoardPart, BoardStat, BoardStrip, type BoardTone } from '../../dashboard/BoardLook'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { AppsAttentionPill } from '../../ui/AppsAttentionMark'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { MaintenanceBadge } from '../AppMaintenance'
import { CriticalityPill, failed, StatePill } from '../parts'
import {
  applicationMapQuery,
  downloadApplicationMapPdf,
  type ApplicationMapPage as MapPageData,
  type MapApp,
  type MapComponent,
  type MapFinding,
  type MapHost,
  type MapLink,
  type Severity,
} from './mapApi'
import { AppLinkLine, AppLinksCard } from './AppLinks'

/**
 * Overview > Applications > Map - TABLOUL aplicatiilor firmei.
 *
 * Pana pe 6 octombrie 2026 pagina era un document (`am-doc`): rezumat in patru
 * dale, constatarile in lista, aplicatiile in carduri cu chenar. Roxana a cerut
 * sa fie dashboard, cu cardurile tablourilor noi. Desenul NU e scris aici: vine
 * din `dashboard/BoardLook.tsx` (aceeasi banda de contoare, aceeasi grila,
 * acelasi card ca Core Applications, SNMP si Productivitee) si din foaia lor,
 * `od_dashboard.css`. Aici se aseaza numai datele hartii in el:
 *
 *   - banda de sus: cifrele firmei, toate din raspunsul hartii (`summary`,
 *     starea aplicatiilor, constatarile pe reguli) si, cand e, disponibilitatea
 *     pe 30 de zile din `GET /business-apps/availability` - acelasi capat pe
 *     care il citeste deja lista Applications;
 *   - partea "Applications": cate un card lat pe aplicatie, cu componentele pe
 *     roluri si masina pe care sta fiecare. Locul cardului poarta `id="app-N"`,
 *     ca legaturile din fisa aplicatiei (`#app-11`) sa duca tot la el;
 *   - partea "Risk findings": un card pe gravitate si unul pe feluri;
 *   - partea "Dependencies": legaturile dintre aplicatii si gazdele fizice.
 *
 * De ce tot fara graf (cytoscape): pagina e si pentru un director, in sedinta.
 * Un graf cu 4 aplicatii si 8 servere are deja muchii care se incruciseaza.
 *
 * Constatarile si rezumatul le calculeaza serverul (`MapFindings`); pagina
 * doar le numara si le aseaza. PDF-ul il face serverul (`MapPaper`), deci nu
 * depinde de desenul de aici. Tiparirea: `business_apps_map.css` ascunde bara
 * si desface celulele de inaltime fixa, ca nimic sa nu se taie pe hartie.
 *
 * IP-urile ies numai cu bifa "Include IP addresses" - si atunci le cere de la
 * server, nu le ascunde doar pe ecran.
 */
const STYLES = ['css/adm.css', 'css/business_apps_map.css']

/** Placuta cardurilor: aplicatiile stau sub Overview. */
const MODULE = 'overview'

const SEVERITY_LABEL: Record<Severity, string> = { high: 'High', medium: 'Medium', low: 'Low' }
const SEVERITY_TONE: Record<Severity, BoardTone> = { high: 'crit', medium: 'warn', low: 'flat' }

/** Felurile de constatari, cu numele lor pe card. Ce nu e aici apare cu numele regulii. */
const RULE_LABEL: Record<string, string> = {
  shared_host: 'Critical apps on one host',
  same_host_redundancy: 'Standby on the same host',
  single_point: 'No standby',
  open_issues: 'Open alarms and tickets',
  shared_component: 'Server shared by apps',
  no_agent: 'No monitoring agent',
  not_seen: 'Not reporting',
  not_in_cmdb: 'Not in the CMDB',
  unknown_host: 'Host unknown',
  support_gaps: 'Support gaps',
  licenses: 'Licenses',
  backup_overdue: 'Backup',
  backup_failed: 'Backup',
  backup_unknown: 'Backup',
  expiring: 'Expiring',
  no_components: 'No components',
}

const BACKUP_RULES = ['backup_overdue', 'backup_failed', 'backup_unknown']

function initialCompany(): number {
  const raw = new URLSearchParams(window.location.search).get('company')
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function ApplicationMapPage() {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [company, setCompany] = useState<number>(initialCompany)
  const [ips, setIps] = useState(false)
  const [busy, setBusy] = useState(false)
  const page = useQuery(applicationMapQuery(company, ips))

  /* `#app-11` din fisa aplicatiei: cardul ei, dupa ce s-a desenat. */
  const loaded = page.data !== undefined
  useEffect(() => {
    if (!loaded || window.location.hash === '') return
    const el = document.getElementById(window.location.hash.slice(1))
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' })
  }, [loaded])

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data

  const pdf = () => {
    setBusy(true)
    downloadApplicationMapPdf(data.companyId, ips)
      .catch((error) => toast(failed(error), 'Applications dashboard', 'error'))
      .finally(() => setBusy(false))
  }

  const pickCompany = (id: number) => {
    setCompany(id)
    const url = new URL(window.location.href)
    if (id > 0) url.searchParams.set('company', String(id))
    else url.searchParams.delete('company')
    window.history.replaceState(null, '', url.toString())
  }

  const companies = data.companies ?? []
  const showing = data.ready && !data.needsCompany

  return (
    <Content>
      <AppsAttentionPill count={shell.data?.appsAttention} />
      <BoardLook>
        <div id="appMap" className="am-board">
          <div className="odb-bar-top">
            <h1 className="odb-name">Applications dashboard{showing && data.companyName ? ` - ${data.companyName}` : ''}</h1>
            <div className="odb-bar-right am-toolbar am-noprint">
              <a href="/app/applications" className="btn btn-default">
                <i className="fas fa-arrow-left"></i> Applications
              </a>
              {companies.length > 0 && (
                <label className="am-company">
                  Customer{' '}
                  <select id="amCompany" className="form-control input-sm" value={data.companyId} onChange={(e) => pickCompany(Number(e.target.value))}>
                    <option value={0}>- choose -</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {showing && (
                <>
                  <label className="am-check">
                    <input type="checkbox" id="amIps" checked={ips} onChange={(e) => setIps(e.target.checked)} /> Include IP addresses
                  </label>
                  <button type="button" className="btn btn-default" id="amPrint" onClick={() => window.print()}>
                    <i className="fas fa-print"></i> Print
                  </button>
                  {data.pdfAvailable && (
                    <button type="button" className="btn btn-primary" id="amPdf" disabled={busy} onClick={pdf}>
                      <i className="fas fa-file-pdf"></i> Download PDF
                    </button>
                  )}
                </>
              )}
            </div>
            {showing && (
              <p className="odb-lead">
                {data.summary && (
                  <>
                    <span id="amSentence">{data.summary.sentence}</span> ·{' '}
                  </>
                )}
                Prepared {data.generatedAt}
              </p>
            )}
          </div>

          {!data.ready ? (
            <div className="odb-empty">
              <i className="fas fa-database"></i> {data.message ?? 'Business applications are not set up on this server yet.'}
            </div>
          ) : data.needsCompany ? (
            <div className="odb-empty" id="amNeedsCompany">
              <i className="fas fa-building"></i> {data.message ?? 'Choose the customer whose application map you want to see.'}
            </div>
          ) : (
            <MapBoard page={data} />
          )}
        </div>
      </BoardLook>
    </Content>
  )
}

/** Aplicatiile atinse acum: cazute sau cu alarma deschisa, ori mergand pe jumatate. */
function affected(apps: MapApp[]) {
  const down = apps.filter((a) => a.state === 'down' || a.state === 'alarm').length
  const degraded = apps.filter((a) => a.state === 'degraded').length
  return { down, degraded, total: down + degraded }
}

/** Cate aplicatii distincte sunt numite in constatarile unor reguli. */
function appsIn(findings: MapFinding[], rules: string[]): number {
  const names = new Set<string>()
  for (const f of findings) if (rules.includes(f.rule)) for (const a of f.apps) names.add(a)
  return names.size
}

/** Tonul celei mai grave constatari; niciuna = ok. */
function worst(findings: MapFinding[]): BoardTone {
  if (findings.some((f) => f.severity === 'high')) return 'crit'
  if (findings.some((f) => f.severity === 'medium')) return 'warn'
  return findings.length > 0 ? 'flat' : 'ok'
}

function MapBoard({ page }: { page: MapPageData }) {
  const apps = page.apps ?? []
  const findings = page.findings ?? []
  const hosts = page.hosts ?? []
  const links = page.links ?? []
  const s = page.summary

  /* Acelasi capat ca lista Applications; fara el banda are o cifra mai putin, nu o eroare. */
  const availability = useQuery({ ...availabilityOverviewQuery(), retry: false })
  const ids = new Set(apps.map((a) => a.id))
  const avById = new Map<number, number | null>()
  if (availability.data?.ready === true && Array.isArray(availability.data.apps)) {
    for (const a of availability.data.apps) if (ids.has(a.appId)) avById.set(a.appId, a.availability)
  }
  const known = [...avById.values()].filter((v): v is number => v !== null)
  const average = known.length > 0 ? known.reduce((x, y) => x + y, 0) / known.length : null

  const hit = affected(apps)
  const backups = appsIn(findings, BACKUP_RULES)
  const expiring = findings.filter((f) => f.rule === 'expiring')

  return (
    <>
      {s && (
        <BoardStrip>
          <BoardStat module={MODULE} icon="fa-layer-group" value={s.apps} kicker={`${s.critical} critical`} title="Applications" />
          <BoardStat
            module={MODULE}
            tone={hit.down > 0 ? 'crit' : hit.degraded > 0 ? 'warn' : 'ok'}
            icon="fa-heartbeat"
            value={hit.total}
            kicker={hit.total === 0 ? 'All running' : `${hit.down} down · ${hit.degraded} degraded`}
            title="Affected now"
          />
          <BoardStat
            module={MODULE}
            tone={s.high > 0 ? 'crit' : s.medium > 0 ? 'warn' : 'ok'}
            icon="fa-exclamation-triangle"
            value={s.findings}
            kicker={`${s.high} high · ${s.medium} medium`}
            title="Risk findings"
          />
          <BoardStat module={MODULE} tone={backups > 0 ? 'warn' : 'ok'} icon="fa-hdd" value={backups} kicker="Applications" title="With backup findings" />
          <BoardStat module={MODULE} tone={worst(expiring)} icon="fa-hourglass-half" value={expiring.length} kicker="Contract, license, TLS" title="Expiring soon" />
          {average !== null && <BoardStat module={MODULE} icon="fa-chart-line" value={formatPercent(average).replace('%', '')} unit="%" kicker="30 days" title="Availability" />}
        </BoardStrip>
      )}

      <BoardPart title="Applications">
        {apps.length === 0 ? (
          <div className="odb-empty">No business application is recorded for this customer yet.</div>
        ) : (
          <BoardGrid id="amApps">
            {apps.map((app) => (
              <AppCard key={app.id} app={app} showIp={page.includeIps} links={links} availability={avById.get(app.id)} />
            ))}
          </BoardGrid>
        )}
      </BoardPart>

      <BoardPart title="Risk findings">
        {findings.length === 0 ? (
          <div className="odb-empty">No risk found in the data we hold.</div>
        ) : (
          <BoardGrid id="amFindingsGrid">
            {(['high', 'medium', 'low'] as Severity[]).map((sev) => (
              <SeverityCard key={sev} severity={sev} findings={findings.filter((f) => f.severity === sev)} />
            ))}
            <KindsCard findings={findings} />
          </BoardGrid>
        )}
      </BoardPart>

      {(links.length > 0 || hosts.length > 0) && (
        <BoardPart title="Dependencies">
          <BoardGrid id="amDependencies">
            <AppLinksCard links={links} />
            {hosts.length > 0 && <HostsCard hosts={hosts} showIp={page.includeIps} />}
          </BoardGrid>
        </BoardPart>
      )}

      <footer className="am-legend">
        <span>
          <i className="odb-dot odb-c-ok"></i> online
        </span>
        <span>
          <i className="odb-dot odb-c-warn"></i> not reporting / degraded
        </span>
        <span>
          <i className="odb-dot odb-c-crit"></i> down or open alarm
        </span>
        <span>
          <span className="am-tag am-tag-shared">Shared</span> used by more than one application
        </span>
      </footer>
    </>
  )
}

/**
 * Constatarile unei gravitati. Randul poarta titlul; sub el, de ce conteaza si
 * ce e de facut - acelasi text ca in PDF.
 */
function SeverityCard({ severity, findings }: { severity: Severity; findings: MapFinding[] }) {
  return (
    <BoardCard
      module={MODULE}
      tone={findings.length === 0 ? 'ok' : SEVERITY_TONE[severity]}
      icon={severity === 'high' ? 'fa-exclamation-circle' : severity === 'medium' ? 'fa-exclamation-triangle' : 'fa-info-circle'}
      value={findings.length}
      kicker="Findings"
      title={`${SEVERITY_LABEL[severity]} severity`}
      tall
    >
      {findings.length === 0 ? (
        <div className="odb-note">Nothing at this severity.</div>
      ) : (
        <BoardList>
          <ol className="am-flist" id={`amFindings-${severity}`}>
            {findings.map((f, i) => (
              <li key={`${f.rule}-${i}`} className="am-find" data-severity={f.severity} data-rule={f.rule}>
                <div className="odb-row">
                  <span className={`odb-dot odb-c-${SEVERITY_TONE[f.severity]}`} aria-hidden="true" />
                  <span className="odb-row-label">{f.title}</span>
                </div>
                <div className="odb-fold">
                  <div className="odb-note">{f.detail}</div>
                  <div className="odb-note">
                    <i className="fas fa-arrow-right" aria-hidden="true"></i> {f.recommendation}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </BoardList>
      )}
    </BoardCard>
  )
}

/** Cate constatari din fiecare fel; bara din spatele randului e fata de cel mai numeros. */
function KindsCard({ findings }: { findings: MapFinding[] }) {
  const kinds = new Map<string, MapFinding[]>()
  for (const f of findings) {
    const label = RULE_LABEL[f.rule] ?? f.rule
    kinds.set(label, [...(kinds.get(label) ?? []), f])
  }
  const rows = [...kinds.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
  const top = rows.length > 0 ? rows[0][1].length : 1

  return (
    <BoardCard module={MODULE} icon="fa-list-ul" value={rows.length} kicker="Findings" title="By kind" tall>
      <BoardList>
        <div id="amKinds">
          {rows.map(([label, list]) => (
            <div className="odb-row" key={label} data-kind={label}>
              <span className="odb-row-fill" style={{ width: `${Math.round((list.length / top) * 100)}%` }} />
              <span className={`odb-dot odb-c-${worst(list)}`} aria-hidden="true" />
              <span className="odb-row-label">{label}</span>
              <span className="odb-row-value">{list.length}</span>
            </div>
          ))}
        </div>
      </BoardList>
    </BoardCard>
  )
}

function componentTone(state: string): 'crit' | 'warn' | 'ok' | 'flat' {
  if (state === 'down' || state === 'alarm' || state === 'offline') return 'crit'
  if (state === 'stale' || state === 'degraded' || state === 'unmonitored') return 'warn'
  if (state === 'online' || state === 'ok') return 'ok'
  return 'flat'
}

/** Culoarea textului de stare, din foaia hartii (`am-comp-state am-st-*`). */
function stateClass(state: string): string {
  return { crit: 'am-st-down', warn: 'am-st-stale', ok: 'am-st-online', flat: 'am-st-unknown' }[componentTone(state)]
}

function appTone(app: MapApp): BoardTone {
  if (app.state === 'down' || app.state === 'alarm') return 'crit'
  if (app.state === 'degraded') return 'warn'
  if (app.state === 'ok') return 'ok'
  return 'unknown'
}

/**
 * O aplicatie, pe doua coloane si doua randuri. Cifra e numarul de servere (fara
 * statii), colorata dupa starea aplicatiei - ca dala din Core Applications.
 * Componentele se deruleaza in card cand sunt multe.
 */
function AppCard({ app, showIp, links, availability }: { app: MapApp; showIp: boolean; links: MapLink[]; availability?: number | null }) {
  const support: string[] = []
  if (app.schedule !== '') support.push(app.schedule)
  if (app.responseHours !== null) support.push(`${app.responseHours}h response`)
  if (app.contractNo !== '') support.push(`contract ${app.contractNo}`)
  const servers = app.groups.filter((g) => g.role !== 4).reduce((n, g) => n + g.components.length, 0)
  const product = [app.vendor, app.version].filter((x) => x !== '').join(' ')

  return (
    <BoardCard
      id={`app-${app.id}`}
      module={MODULE}
      tone={appTone(app)}
      icon="fa-cubes"
      value={servers}
      unit={<i className="far fa-hdd" aria-hidden="true" />}
      kicker={product !== '' ? product : 'Business application'}
      title={app.name}
      wide
      tall={app.groups.length > 0}
      foot={
        <>
          <span className="odb-of">
            {app.openAlarms} alarm{app.openAlarms === 1 ? '' : 's'}
          </span>
          <span className="odb-of">
            {app.openTickets} ticket{app.openTickets === 1 ? '' : 's'}
          </span>
          {availability !== undefined && <span className="odb-of am-avail">30-day availability {formatPercent(availability)}</span>}
        </>
      }
    >
      <BoardList>
        <div className="am-app-pills">
          <CriticalityPill value={app.criticality} label={app.criticalityLabel} />
          <StatePill state={app.state} label={app.stateLabel} />
          <MaintenanceBadge appId={app.id} />
        </div>
        <div className="am-support">
          <span className="am-label">Support</span>{' '}
          {app.supportVendor !== '' ? <strong>{app.supportVendor}</strong> : <span className="am-missing">not recorded</span>}
          {support.length > 0 && <span className="am-muted"> · {support.join(' · ')}</span>}
        </div>
        <AppLinkLine appId={app.id} links={links} />
        {app.groups.length === 0 ? (
          <div className="odb-note">No component recorded.</div>
        ) : (
          app.groups.map((g) => (
            <div className="am-group" key={g.role}>
              <div className="am-group-label">{g.roleLabel}</div>
              {g.role === 4 && g.components.length > 4 ? (
                <div className="am-ws">
                  {g.components.map((c) => (
                    <span key={c.key} className="am-ws-item" title={c.stateLabel}>
                      <i className={`odb-dot odb-c-${componentTone(c.state)}`}></i>
                      {c.name}
                    </span>
                  ))}
                </div>
              ) : (
                g.components.map((c) => <ComponentRow key={c.key} c={c} showIp={showIp} workstation={g.role === 4} />)
              )}
            </div>
          ))
        )}
      </BoardList>
    </BoardCard>
  )
}

/*
 * O statie folosita de doua aplicatii e obisnuita (receptia ruleaza si Opera
 * si Charisma), iar statiile nu intra in CMDB: etichetele "Shared" si "Not in
 * CMDB" se pun numai pe servere, unde chiar spun un risc.
 */
function ComponentRow({ c, showIp, workstation }: { c: MapComponent; showIp: boolean; workstation: boolean }) {
  const shared = !workstation && c.sharedWith.length > 0
  const where =
    c.hostName !== '' ? (
      <span>
        Runs on <strong>{c.hostName}</strong>
      </span>
    ) : c.virtual ? (
      <span className="am-missing">Virtual machine, host unknown</span>
    ) : c.deviceId !== null ? (
      <span>Physical</span>
    ) : null

  return (
    <div className={`am-cmp${shared ? ' am-cmp-shared' : ''}`} data-comp={c.key}>
      <div className="odb-row">
        <span className={`odb-dot odb-c-${componentTone(c.state)}`} aria-hidden="true" />
        <span className="odb-row-label">{c.name}</span>
        {shared && (
          <span className="am-tag am-tag-shared" title={`Also used by ${c.sharedWith.join(', ')}`}>
            Shared
          </span>
        )}
        {!c.inCmdb && !workstation && <span className="am-tag am-tag-warn">Not in CMDB</span>}
        {c.openTickets > 0 && (
          <span className="am-tag">
            {c.openTickets} ticket{c.openTickets === 1 ? '' : 's'}
          </span>
        )}
        <span className={`am-comp-state ${stateClass(c.state)}`}>{c.stateLabel}</span>
      </div>
      <div className="odb-fold am-cmp-meta">
        {where}
        {c.office !== '' && <span>{c.office}</span>}
        {showIp && c.ip && <span className="am-ip">{c.ip}</span>}
        {shared && <span className="am-shared-with">also {c.sharedWith.join(', ')}</span>}
      </div>
    </div>
  )
}

/** Gazdele fizice: un rand pe gazda, cu masinile virtuale si aplicatiile lor dedesubt. */
function HostsCard({ hosts, showIp }: { hosts: MapHost[]; showIp: boolean }) {
  const risky = hosts.filter((h) => h.criticalApps >= 2).length
  return (
    <BoardCard
      module={MODULE}
      tone={risky > 0 ? 'warn' : 'flat'}
      icon="fa-server"
      value={hosts.length}
      kicker={risky > 0 ? `${risky} with 2+ critical apps` : 'Virtual machines by host'}
      title="Physical hosts"
      wide
      tall={hosts.length > 1}
    >
      <BoardList>
        {hosts.map((h) => (
          <div className="am-host-row" key={h.id} data-host={h.id}>
            <div className="odb-row">
              <span className={`odb-dot odb-c-${componentTone(h.state)}`} aria-hidden="true" />
              <span className="odb-row-label">
                <strong>{h.name}</strong>
                {h.office !== '' && <span className="am-muted"> · {h.office}</span>}
                {showIp && h.ip ? <span className="am-ip"> · {h.ip}</span> : null}
              </span>
              {h.criticalApps >= 2 && <span className="am-tag am-tag-risk">{h.criticalApps} critical apps</span>}
              <span className={`am-comp-state ${stateClass(h.state)}`}>{h.stateLabel}</span>
            </div>
            <ul className="odb-fold am-guests">
              {h.guests.map((g) => (
                <li key={g.name} className="odb-note">
                  <strong>{g.name}</strong> <span className="am-muted">- {g.apps.join(', ')}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </BoardList>
    </BoardCard>
  )
}
